import { Axios } from 'axios'
import { RootState } from '@/store'
import {
  FacilityInfoPick,
  ResponseShokushuRegisterLayout,
  ResponseShokushuShukeiData,
  ShokushuKmkType
} from '@/repositories/keiei-joho/shokushu/data'
import { ShokushuKmkTypeType } from '@/repositories/keiei-joho/data'

type PostItem = {
  kmkType: ShokushuKmkTypeType;
  kin: number | null;
}
type PostParamItem = {
  startDate: string;
  endDate: string;
  facilityKey: string;
  kmkTypes: PostItem[];
}

type ResponseHasData = {
  hasCorporationInfo: boolean,
  hasFacilityInfo: boolean,
  hasSettingData: boolean
}

export interface ShokushuRegisterRepository {
  checkDataExists (): Promise<ResponseHasData>;
  getFacilityInfoPick (): Promise<FacilityInfoPick[]>;
  getKmkTypes (facilityKey: string): Promise<ResponseShokushuRegisterLayout>;
  save (facilityKey: string, allKmkTypes: ShokushuKmkType[]): Promise<void>;
  recalculate (facilityKey: string): Promise<ResponseShokushuRegisterLayout>;
  copy (facilityKey: string): Promise<ResponseShokushuShukeiData[]>;
}

export class ShokushuRegisterRepositoryImpl implements ShokushuRegisterRepository {
  constructor (http: Axios, baseUrl: string, state: RootState) {
    this.http = http
    this.baseUrl = `${baseUrl}/shokushu/register`
    this.state = state
  }

  private readonly http: Axios

  private readonly baseUrl: string

  private readonly state: RootState

  /**
   * 法人情報と施設情報と科目設定の存在チェック
   */
  async checkDataExists (): Promise<ResponseHasData> {
    const { data } = await this.http.get(`${this.baseUrl}/check-data-exists`)
    return data
  }

  /**
   * 施設情報のデータ取得
   */
  async getFacilityInfoPick (): Promise<FacilityInfoPick[]> {
    const { data } = await this.http.get(`${this.baseUrl}/facility-info`)
    return data
  }

  /**
   * 職種別科目タイプと法人情報と施設情報のデータ取得
   */
  async getKmkTypes (facilityKey: string): Promise<ResponseShokushuRegisterLayout> {
    const startDate = this.state.accountingStartDate
    const endDate = this.state.accountingEndDate
    const { data } = await this.http.get(`${this.baseUrl}/${startDate}/${endDate}/${facilityKey}`)
    return data
  }

  /**
   * 登録
   */
  async save (facilityKey: string, allKmkTypes: ShokushuKmkType[]): Promise<void> {
    const startDate = this.state.accountingStartDate
    const endDate = this.state.accountingEndDate
    const items: PostItem[] = allKmkTypes.map(p => ({
      kmkType: p.kmkType,
      kin: p.kin
    }))
    const postParam: PostParamItem = {
      startDate: startDate,
      endDate: endDate,
      facilityKey: facilityKey,
      kmkTypes: items
    }
    await this.http.post(`${this.baseUrl}`, postParam)
  }

  /**
   * 再集計
   */
  async recalculate (facilityKey: string): Promise<ResponseShokushuRegisterLayout> {
    const startDate = this.state.accountingStartDate
    const endDate = this.state.accountingEndDate
    const { data } = await this.http.get(`${this.baseUrl}/recalculate/${startDate}/${endDate}/${facilityKey}`)
    return data
  }

  /**
   * 人数複写
   */
  async copy (facilityKey: string): Promise<ResponseShokushuShukeiData[]> {
    const startDate = this.state.accountingStartDate
    const endDate = this.state.accountingEndDate
    const { data } = await this.http.get(`${this.baseUrl}/copy/${startDate}/${endDate}/${facilityKey}`)
    return data
  }
}
